import React from 'react';
import {
  Box, Flex, Grid, GridItem, Text, Stat, StatLabel, StatNumber,
  StatHelpText, StatArrow, Icon, useColorModeValue, VStack,
  HStack, Avatar, Heading, Card, CardHeader, CardBody
} from '@chakra-ui/react';
import {
  MdPeople, MdFolder, MdTrendingUp, MdAttachMoney,
  MdAssignment, MdCheckCircle, MdPending, MdCancel
} from 'react-icons/md';

import './AdminDashboard.css';

const StatCard = ({ icon, label, value, change, changeType }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue('brand.primary', 'brand.secondary');

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      shadow="sm"
      transition="all 0.3s"
      _hover={{ shadow: 'md' }}
    >
      <Stat>
        <Flex justify="space-between" align="flex-start">
          <Box>
            <StatLabel fontSize="sm" color="gray.500">{label}</StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold" mt={2}>{value}</StatNumber>
            {change && (
              <StatHelpText mt={2}>
                <StatArrow type={changeType} />
                {change}
              </StatHelpText>
            )}
          </Box>
          <Icon as={icon} fontSize="40px" color={iconColor} />
        </Flex>
      </Stat>
    </Box>
  );
};

const AdminDashboard = () => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');

  return (
    <Box p={{ base: 4, md: 8 }}>
          {/* Header */}
          <Flex justify="space-between" align="center" mb={8}>
            <Box>
              <Heading size="lg" color={textColor}>
                Dashboard Administrativo
              </Heading>
              <Text color="gray.500" mt={1}>
                Bem-vindo de volta, {userInfo?.name || 'Administrador'}!
              </Text>
            </Box>
            <HStack spacing={3}>
              <Avatar
                size="md"
                name={userInfo?.name || 'Admin'}
                src={userInfo?.avatar || ''}
                bg="purple.500"
              />
            </HStack>
          </Flex>

          {/* Stats Grid */}
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
            gap={6}
            mb={8}
          >
            <GridItem>
              <StatCard
                icon={MdPeople}
                label="Total de Usuários"
                value="156"
                change="12% desde o mês passado"
                changeType="increase"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdFolder}
                label="Clientes Ativos"
                value="1,234"
                change="8% desde o mês passado"
                changeType="increase"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdAttachMoney}
                label="Valor Total"
                value="R$ 2.5M"
                change="23% desde o mês passado"
                changeType="increase"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdTrendingUp}
                label="Taxa de Conversão"
                value="68%"
                change="5% desde o mês passado"
                changeType="decrease"
              />
            </GridItem>
          </Grid>

          {/* Recent Activity and Status */}
          <Grid
            templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
            gap={6}
          >
            {/* Recent Activity */}
            <GridItem>
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Atividade Recente</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                      <HStack>
                        <Icon as={MdCheckCircle} color="green.500" />
                        <Box>
                          <Text fontWeight="medium">Novo cliente cadastrado</Text>
                          <Text fontSize="sm" color="gray.500">João Silva - há 2 horas</Text>
                        </Box>
                      </HStack>
                    </HStack>
                    <HStack justify="space-between">
                      <HStack>
                        <Icon as={MdAssignment} color="blue.500" />
                        <Box>
                          <Text fontWeight="medium">Precatório analisado</Text>
                          <Text fontSize="sm" color="gray.500">Processo #1234 - há 4 horas</Text>
                        </Box>
                      </HStack>
                    </HStack>
                    <HStack justify="space-between">
                      <HStack>
                        <Icon as={MdPending} color="orange.500" />
                        <Box>
                          <Text fontWeight="medium">Documento pendente</Text>
                          <Text fontSize="sm" color="gray.500">Maria Santos - há 6 horas</Text>
                        </Box>
                      </HStack>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Status Overview */}
            <GridItem>
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Visão Geral de Status</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                      <HStack>
                        <Icon as={MdCheckCircle} color="green.500" fontSize="24px" />
                        <Text>Aprovados</Text>
                      </HStack>
                      <Text fontWeight="bold">245</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <HStack>
                        <Icon as={MdPending} color="orange.500" fontSize="24px" />
                        <Text>Em Análise</Text>
                      </HStack>
                      <Text fontWeight="bold">89</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <HStack>
                        <Icon as={MdCancel} color="red.500" fontSize="24px" />
                        <Text>Rejeitados</Text>
                      </HStack>
                      <Text fontWeight="bold">12</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
    </Box>
  );
};

export default AdminDashboard;
